import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {Markets} from 'nutshell-core/markets';
import {orderProductsByMarketAvailability} from 'nutshell-core/order-products-by-market-availability';
import {Products} from 'nutshell-core/products';
import {Session} from 'nutshell-core/session';
import type {Market, Product, ProductId} from 'nutshell-core/types';

import {OptionWithSubtitleAndSublabel} from 'shells/select-option-components/option-with-subtitle-and-sublabel';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import type {FooterButtonProps} from 'shells/select-picker-generic/select-picker-generic';
import type {SelectSize} from 'shells/select-picker-generic/select-picker-generic-creatable';

import {appHistory} from '../../../history';

import type {ProductPickerProduct} from './graphql/helpers/product-picker-product';
import {useGetProductPickerProducts} from './graphql/hooks/use-get-product-picker-products';
import {useGetProductPickerSuggestions} from './graphql/hooks/use-get-product-picker-suggestions';
import {ProductSearchExclusionLimitError} from './graphql/hooks/use-search-products';
import {MIN_PRODUCT_SEARCH_LENGTH, useSearchProducts} from './graphql/hooks/use-search-products';
import {
    paginatedProductMenuStyles,
    usePaginatedProductMenu,
} from './hooks/use-paginated-product-menu';
import {useProductPickerSelection} from './hooks/use-product-picker-selection';
import {PaginatedProductMenuStatus} from './paginated-product-menu-status';
import {getBrowsableProductOptions, getProductOptions} from './product-picker-options';
import type {ProductOption, ProductOptionsContext} from './product-picker-options';

export type {ProductOption} from './product-picker-options';

interface Props {
    onChange: (option: ProductOption) => void;
    onSelectedProductLoad?: (product: ProductPickerProduct | null) => void;
    selectedId: string | null | undefined;
    size?: SelectSize;
    hideDivider?: boolean;
    defaultMenuIsOpen?: boolean;
    placeholder?: string;
    currentMarket?: Market | null | undefined;
    /** Product relationship filters search the whole catalog, regardless of market pricing. */
    shouldFilterByMarket?: boolean;
    excludeProductIds?: ProductId[];
    autoFocus?: boolean;
    inputId?: string;
    isDisabled?: boolean;
    openMenuOnFocus?: boolean;
    onBlur?: () => void;
    onClear?: () => void;
    getRef?: (ref?: any) => void;
    footerButton?: FooterButtonProps;
    maxMenuHeight?: number;
    noOptionsMessage?: (input?: {inputValue?: string}) => string;
    menuZIndex?: number;
    menuPortalTarget?: HTMLElement;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    /** Enables the grouped recommendations and paginated catalog entry experience. */
    enableProductBrowsing?: boolean;
}

function reportProductSearchError(error: unknown): void {
    const windowWithTrackJs = window as Window & {
        trackJs?: {track: (trackedError: unknown) => void};
    };
    windowWithTrackJs.trackJs?.track(error);
}

function getDefaultNoOptionsMessage({inputValue = ''}: {inputValue?: string} = {}): string {
    return inputValue.trim().length < MIN_PRODUCT_SEARCH_LENGTH
        ? 'Enter at least 2 characters'
        : 'No products found';
}

function toLegacyProductPickerProduct(
    product: Product,
    currentMarket: Market | null | undefined
): ProductPickerProduct {
    return {
        id: product.id,
        name: product.name,
        sku: product.sku,
        category: product.category
            ? {
                  id: product.category.id,
                  name: product.category.name,
              }
            : null,
        priceMarketId: currentMarket?.id ?? null,
        prices: product.prices,
    };
}

function LegacyReduxSelectProducts(props: Props) {
    const {onSelectedProductLoad} = props;
    const allProducts = useSelector((state) => Products.getAll(state)) as Product[];
    // @ts-expect-error
    const productsState = useSelector((state) => state.products);
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    // @ts-expect-error
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const currentMarket =
        props.shouldFilterByMarket === false ? null : props.currentMarket || defaultMarket;

    let filteredProducts = allProducts;
    if (currentMarket?.id) {
        filteredProducts = allProducts.filter((product) => product.prices[currentMarket.id]);
    }

    const selectedLegacyProduct = props.selectedId
        ? allProducts.find((product) => product.id === props.selectedId)
        : undefined;
    if (
        selectedLegacyProduct &&
        !filteredProducts.some((product) => product.id === selectedLegacyProduct.id)
    ) {
        filteredProducts = [...filteredProducts, selectedLegacyProduct];
    }

    const excludedIds = new Set(
        (props.excludeProductIds || []).filter((id) => id !== props.selectedId)
    );
    filteredProducts = filteredProducts.filter((product) => !excludedIds.has(product.id));

    const orderedProducts = currentMarket?.id
        ? orderProductsByMarketAvailability(filteredProducts, currentMarket.id)
        : filteredProducts;
    const productOptions: ProductOption[] = orderedProducts.map(
        (product: Product & {isDisabled?: boolean}) => {
            const pickerProduct = toLegacyProductPickerProduct(product, currentMarket);

            return {
                label: pickerProduct.name,
                value: pickerProduct.id,
                subtitle: pickerProduct.sku || '',
                sublabel: pickerProduct.category?.name || 'No category',
                product: pickerProduct,
                isDisabled: product.isDisabled,
            };
        }
    );
    const selectedProduct = useMemo(
        () =>
            selectedLegacyProduct
                ? toLegacyProductPickerProduct(selectedLegacyProduct, currentMarket)
                : null,
        [currentMarket, selectedLegacyProduct]
    );

    useEffect(() => {
        onSelectedProductLoad?.(selectedProduct);
    }, [onSelectedProductLoad, selectedProduct]);

    const defaultFooterButton = loggedInUser &&
        loggedInUser.permissions.canAccessSetup && {
            label: 'Manage products',
            onClick: () => appHistory.push('/sales/product'),
        };
    const defaultNoOptionsMessage = () => {
        if (allProducts.length && !filteredProducts.length && currentMarket) {
            return 'No products in current market';
        }

        return 'No products found';
    };

    return (
        <SelectPickerGeneric
            isLoading={productsState ? productsState.isLoading : false}
            inputId={props.inputId}
            isDisabled={props.isDisabled}
            autoFocus={props.autoFocus !== undefined ? props.autoFocus : true}
            openMenuOnFocus={props.openMenuOnFocus}
            defaultMenuIsOpen={
                props.defaultMenuIsOpen !== undefined ? props.defaultMenuIsOpen : true
            }
            placeholder={props.placeholder || 'Search product, SKU, or category…'}
            options={productOptions}
            value={productOptions.find((option) => option.value === props.selectedId) || null}
            labelKey='label'
            valueKey='value'
            // @ts-expect-error
            filterOption={(option, search) => {
                if (option.data) {
                    return (
                        (option.data.subtitle &&
                            option.data.subtitle.toLowerCase().includes(search.toLowerCase())) ||
                        (option.data.label &&
                            option.data.label.toLowerCase().includes(search.toLowerCase())) ||
                        (option.data.sublabel &&
                            option.data.sublabel.toLowerCase().includes(search.toLowerCase()))
                    );
                }

                return option.label.toLowerCase().includes(search.toLowerCase());
            }}
            components={{Option: OptionWithSubtitleAndSublabel}}
            isOptionDisabled={(option: ProductOption) => Boolean(option.isDisabled)}
            onChange={props.onChange}
            size={props.size}
            hideDivider={props.hideDivider}
            onBlur={props.onBlur}
            onClear={props.onClear}
            getRef={props.getRef}
            footerButton={props.footerButton || defaultFooterButton}
            maxMenuHeight={props.maxMenuHeight}
            noOptionsMessage={props.noOptionsMessage || defaultNoOptionsMessage}
            menuZIndex={props.menuZIndex}
            menuPortalTarget={props.menuPortalTarget}
            menuPlacement={props.menuPlacement}
        />
    );
}

function SearchOnlyRemoteReduxSelectProducts(props: Props) {
    const {noOptionsMessage: customNoOptionsMessage, onChange} = props;
    const [showSuggestions, setShowSuggestions] = useState<boolean>(true);
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    // @ts-expect-error
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const currentMarket =
        props.shouldFilterByMarket === false ? null : props.currentMarket || defaultMarket;
    const excludeProductIds = props.excludeProductIds || [];
    const omittedProductIds = excludeProductIds.filter((id) => id !== props.selectedId);
    const omittedProductIdsKey = JSON.stringify([...omittedProductIds].sort());
    const omittedProductIdsSet = useMemo(
        () => new Set(JSON.parse(omittedProductIdsKey) as ProductId[]),
        [omittedProductIdsKey]
    );
    const searchContextKey = currentMarket?.id ?? 'no-market';
    const {searchProducts, error: searchError} = useSearchProducts({
        marketId: currentMarket?.id,
        omittedProductIds,
    });
    const {getProductPickerSuggestions} = useGetProductPickerSuggestions({
        marketId: currentMarket?.id,
    });
    const {
        selectedProduct,
        isLoading: isHydratingSelection,
        hasError: hasSelectionHydrationError,
        retry: retrySelectionHydration,
        selectProduct,
    } = useProductPickerSelection({
        selectedId: props.selectedId,
        marketId: currentMarket?.id,
        onSelectedProductLoad: props.onSelectedProductLoad,
    });
    const optionsContext = useRef<ProductOptionsContext>({
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    });
    optionsContext.current = {
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    };

    useEffect(() => {
        if (searchError && !(searchError instanceof ProductSearchExclusionLimitError)) {
            reportProductSearchError(searchError);
        }
    }, [searchError]);

    const loadOptions = useCallback(
        async (query: string): Promise<ProductOption[]> => {
            if (query.trim() === '') {
                try {
                    const products = await getProductPickerSuggestions();
                    return getProductOptions(products, optionsContext.current);
                } catch (error) {
                    reportProductSearchError(error);
                    return getProductOptions([], optionsContext.current);
                }
            }

            const products = await searchProducts(query);
            return products ? getProductOptions(products, optionsContext.current) : [];
        },
        [getProductPickerSuggestions, searchProducts]
    );

    const handleChange = useCallback(
        (option: ProductOption) => {
            if (
                option.product.priceMarketId !== (currentMarket?.id ?? null) ||
                omittedProductIdsSet.has(option.product.id)
            ) {
                return;
            }

            selectProduct(option.product);
            onChange(option);
        },
        [currentMarket?.id, omittedProductIdsSet, onChange, selectProduct]
    );

    const noOptionsMessage = useCallback(
        (input: {inputValue?: string} = {}) => {
            if (searchError) {
                return searchError instanceof ProductSearchExclusionLimitError
                    ? searchError.message
                    : 'Could not load products. Try searching again.';
            }
            if (hasSelectionHydrationError) {
                return 'Could not load the selected product. Open the menu to retry.';
            }

            return customNoOptionsMessage
                ? customNoOptionsMessage(input)
                : getDefaultNoOptionsMessage(input);
        },
        [customNoOptionsMessage, hasSelectionHydrationError, searchError]
    );

    const selectedOption = selectedProduct
        ? getProductOptions([selectedProduct], optionsContext.current)[0] || null
        : null;
    const defaultFooterButton = loggedInUser &&
        loggedInUser.permissions.canAccessSetup && {
            label: 'Manage products',
            onClick: () => appHistory.push('/sales/product'),
        };

    return (
        <SelectPickerGeneric
            key={searchContextKey}
            isLoading={isHydratingSelection}
            hasError={Boolean(searchError) || hasSelectionHydrationError}
            inputId={props.inputId}
            isDisabled={props.isDisabled}
            autoFocus={props.autoFocus !== undefined ? props.autoFocus : true}
            openMenuOnFocus={props.openMenuOnFocus}
            defaultMenuIsOpen={
                props.defaultMenuIsOpen !== undefined ? props.defaultMenuIsOpen : true
            }
            placeholder={props.placeholder || 'Search product, SKU, or category…'}
            loadOptions={loadOptions}
            onInputChange={(inputValue: string) => setShowSuggestions(inputValue.trim() === '')}
            menuListHeader={showSuggestions ? 'Most frequently used products' : undefined}
            value={selectedOption}
            labelKey='label'
            valueKey='value'
            components={{Option: OptionWithSubtitleAndSublabel}}
            isOptionDisabled={(option: ProductOption) =>
                Boolean(option.isDisabled) || omittedProductIdsSet.has(option.product.id)
            }
            onChange={handleChange}
            onMenuOpen={hasSelectionHydrationError ? retrySelectionHydration : undefined}
            size={props.size}
            hideDivider={props.hideDivider}
            onBlur={props.onBlur}
            onClear={props.onClear}
            getRef={props.getRef}
            footerButton={props.footerButton || defaultFooterButton}
            maxMenuHeight={props.maxMenuHeight}
            noOptionsMessage={noOptionsMessage}
            menuZIndex={props.menuZIndex}
            menuPortalTarget={props.menuPortalTarget}
            menuPlacement={props.menuPlacement}
        />
    );
}

function BrowsableRemoteReduxSelectProducts(props: Props) {
    const {onChange} = props;
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    // @ts-expect-error
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const currentMarket =
        props.shouldFilterByMarket === false ? null : props.currentMarket || defaultMarket;
    const excludeProductIds = props.excludeProductIds || [];
    const omittedProductIds = excludeProductIds.filter((id) => id !== props.selectedId);
    const omittedProductIdsKey = JSON.stringify([...omittedProductIds].sort());
    const stableOmittedProductIds = useMemo(
        () => JSON.parse(omittedProductIdsKey) as ProductId[],
        [omittedProductIdsKey]
    );
    const omittedProductIdsSet = useMemo(
        () => new Set(stableOmittedProductIds),
        [stableOmittedProductIds]
    );
    const defaultMenuIsOpen = props.defaultMenuIsOpen ?? true;
    const {getProductPickerSuggestions} = useGetProductPickerSuggestions({
        marketId: currentMarket?.id,
    });
    const getRecommendations = useCallback(
        async () => (await getProductPickerSuggestions()).slice(0, 10),
        [getProductPickerSuggestions]
    );
    const {getProductPickerProducts} = useGetProductPickerProducts({
        marketId: currentMarket?.id,
        omittedProductIds: stableOmittedProductIds,
    });
    const {
        selectedProduct,
        isLoading: isHydratingSelection,
        hasError: hasSelectionHydrationError,
        retry: retrySelectionHydration,
        selectProduct,
    } = useProductPickerSelection({
        selectedId: props.selectedId,
        marketId: currentMarket?.id,
        onSelectedProductLoad: props.onSelectedProductLoad,
    });
    const optionsContext = useRef<ProductOptionsContext>({
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    });
    optionsContext.current = {
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    };
    const toOptions = useCallback(
        (products: ProductPickerProduct[]) =>
            getBrowsableProductOptions(products, optionsContext.current),
        []
    );
    const menu = usePaginatedProductMenu<ProductPickerProduct, ProductOption>({
        contextKey: `${currentMarket?.id ?? 'no-market'}:${omittedProductIdsKey}`,
        minimumSearchLength: MIN_PRODUCT_SEARCH_LENGTH,
        initialMenuIsOpen: defaultMenuIsOpen,
        getRecommendations,
        getPage: getProductPickerProducts,
        getItemId: (product) => product.id,
        toOptions,
    });
    const selectedOption = selectedProduct
        ? getProductOptions([selectedProduct], optionsContext.current)[0] || null
        : null;
    const defaultFooterButton = loggedInUser &&
        loggedInUser.permissions.canAccessSetup && {
            label: 'Manage products',
            onClick: () => appHistory.push('/sales/product'),
        };
    const handleChange = useCallback(
        (option: ProductOption) => {
            if (
                option.product.priceMarketId !== (currentMarket?.id ?? null) ||
                omittedProductIdsSet.has(option.product.id)
            ) {
                return;
            }

            selectProduct(option.product);
            onChange(option);
        },
        [currentMarket?.id, omittedProductIdsSet, onChange, selectProduct]
    );
    const handleMenuOpen = useCallback(() => {
        menu.onMenuOpen();
        if (hasSelectionHydrationError) retrySelectionHydration();
    }, [hasSelectionHydrationError, menu, retrySelectionHydration]);
    const noOptionsMessage = useCallback(
        ({inputValue = ''}: {inputValue?: string} = {}) => {
            if (hasSelectionHydrationError) {
                return 'Could not load the selected product. Open the menu to retry.';
            }
            if (inputValue.trim().length > 0 && inputValue.trim().length < 2) {
                return 'Enter at least 2 characters';
            }
            if (menu.pageError) return 'Products could not be loaded.';

            return 'No products found';
        },
        [hasSelectionHydrationError, menu.pageError]
    );

    return (
        <SelectPickerGeneric
            key={currentMarket?.id ?? 'no-market'}
            isLoading={isHydratingSelection || menu.isLoading}
            hasError={
                hasSelectionHydrationError ||
                Boolean(menu.recommendationError || menu.pageError || menu.nextPageError)
            }
            inputId={props.inputId}
            isDisabled={props.isDisabled}
            autoFocus={props.autoFocus !== undefined ? props.autoFocus : true}
            openMenuOnFocus={props.openMenuOnFocus}
            defaultMenuIsOpen={defaultMenuIsOpen}
            placeholder={props.placeholder || 'Search product, SKU, or category…'}
            options={menu.groups}
            inputValue={menu.inputValue}
            onInputChange={menu.onInputChange}
            filterOption={null}
            value={selectedOption}
            labelKey='label'
            valueKey='value'
            components={{Option: OptionWithSubtitleAndSublabel}}
            isOptionDisabled={(option: ProductOption) =>
                Boolean(option.isDisabled) || omittedProductIdsSet.has(option.product.id)
            }
            onChange={handleChange}
            onMenuOpen={handleMenuOpen}
            onMenuClose={menu.onMenuClose}
            onMenuScrollToBottom={menu.onMenuScrollToBottom}
            styles={paginatedProductMenuStyles}
            menuListFooter={
                <PaginatedProductMenuStatus
                    recommendationError={menu.recommendationError}
                    pageError={menu.pageError}
                    nextPageError={menu.nextPageError}
                    isLoadingNextPage={menu.isLoadingNextPage}
                    retryRecommendations={menu.retryRecommendations}
                    retryPage={menu.retryPage}
                />
            }
            size={props.size}
            hideDivider={props.hideDivider}
            onBlur={props.onBlur}
            onClear={props.onClear}
            getRef={props.getRef}
            footerButton={props.footerButton || defaultFooterButton}
            maxMenuHeight={props.maxMenuHeight}
            noOptionsMessage={noOptionsMessage}
            menuZIndex={props.menuZIndex}
            menuPortalTarget={props.menuPortalTarget}
            menuPlacement={props.menuPlacement}
        />
    );
}

function RemoteReduxSelectProducts(props: Props) {
    return props.enableProductBrowsing ? (
        <BrowsableRemoteReduxSelectProducts {...props} />
    ) : (
        <SearchOnlyRemoteReduxSelectProducts {...props} />
    );
}

export function ReduxSelectProducts(props: Props) {
    return NutClientConfig.hasProductListImprovementsFF ? (
        <RemoteReduxSelectProducts {...props} />
    ) : (
        <LegacyReduxSelectProducts {...props} />
    );
}
